/* collapsible-description.js — <collapsible-description>

   Expand / collapse toggle for the PDP description (snippets/
   collapsible-product-description.liquid). The Liquid renders the full text
   plus a toggle button; CSS clamps the text to N lines with a fade while the
   element carries neither .is-expanded nor .is-static.

   Two things only the browser can decide:

   1. Whether there is anything to reveal. The Liquid gate is a rough character
      count, so a description that fits the clamp (short lines, few paragraphs)
      still arrives with a toggle. Measured here: if the clamped box is not
      actually hiding anything the element becomes static and the toggle
      disappears, so "Mehr anzeigen" never opens onto nothing.

   2. The height animation. max-height cannot animate to `auto`, so the target
      height is measured and set inline for the duration of the transition,
      then cleared so the content can reflow freely.

   Without JS the CSS clamp still applies but the button does nothing; the
   full text is still in the DOM (find-in-page, reader mode, search engines). */

const TOLERANCE = 4;

class CollapsibleDescription extends HTMLElement {
  constructor() {
    super();
    this.content = this.querySelector('.description-disclosure__content');
    this.toggle = this.querySelector('.description-disclosure__toggle');
    this.onToggle = this.onToggle.bind(this);
    this.onTransitionEnd = this.onTransitionEnd.bind(this);
  }

  connectedCallback() {
    if (!this.content || !this.toggle) return;
    this.toggle.addEventListener('click', this.onToggle);
    this.content.addEventListener('transitionend', this.onTransitionEnd);
    this.measure();
    /* Fonts loading after first paint can change the line count. */
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => this.measure());
    }
  }

  disconnectedCallback() {
    this.toggle?.removeEventListener('click', this.onToggle);
    this.content?.removeEventListener('transitionend', this.onTransitionEnd);
  }

  get expanded() {
    return this.classList.contains('is-expanded');
  }

  /* Only meaningful while collapsed: compare the clamped box with the text
     inside it. Once expanded the two are equal by definition. */
  measure() {
    if (this.expanded || this.classList.contains('is-static')) return;
    const hidden = this.content.scrollHeight - this.content.clientHeight;
    if (hidden <= TOLERANCE) {
      this.classList.add('is-static');
      this.toggle.hidden = true;
    }
  }

  get animates() {
    return !Theme.config.motionReduced;
  }

  onToggle() {
    this.expanded ? this.collapse() : this.expand();
  }

  /* The start height is committed with a forced reflow rather than a
     requestAnimationFrame pair: rAF does not run in a backgrounded tab, which
     would leave the element stuck mid-toggle until the next click. */
  expand() {
    const from = this.content.clientHeight;
    this.classList.add('is-expanded');
    this.toggle.setAttribute('aria-expanded', 'true');
    if (!this.animates) return;
    this.classList.add('is-animating');
    this.content.style.maxHeight = `${from}px`;
    void this.content.offsetHeight;
    this.content.style.maxHeight = `${this.content.scrollHeight}px`;
    this.armFallback();
  }

  collapse() {
    if (!this.animates) {
      this.finishCollapse();
      return;
    }
    this.classList.add('is-animating');
    this.content.style.maxHeight = `${this.content.scrollHeight}px`;
    void this.content.offsetHeight;
    this.classList.remove('is-expanded');
    this.toggle.setAttribute('aria-expanded', 'false');
    /* Clearing the inline value hands control back to the stylesheet clamp,
       so the transition runs from the full height down to it. */
    this.content.style.maxHeight = '';
    this.armFallback();
  }

  finishCollapse() {
    this.classList.remove('is-expanded', 'is-animating');
    this.toggle.setAttribute('aria-expanded', 'false');
    this.content.style.maxHeight = '';
    /* The toggle keeps focus; make sure it is still on screen after the text
       above it shrank, so a keyboard user is not left looking at the price. */
    const rect = this.getBoundingClientRect();
    if (rect.top < 0) this.scrollIntoView({ block: 'start', behavior: this.animates ? 'smooth' : 'auto' });
  }

  finish() {
    clearTimeout(this.fallback);
    this.classList.remove('is-animating');
    if (this.expanded) {
      this.content.style.maxHeight = '';
    } else {
      this.finishCollapse();
    }
  }

  /* transitionend does not fire when the two heights are equal or the tab is
     hidden, so the state is settled after the transition duration regardless. */
  armFallback() {
    clearTimeout(this.fallback);
    this.fallback = setTimeout(() => this.finish(), 400);
  }

  onTransitionEnd(event) {
    if (event.target !== this.content || event.propertyName !== 'max-height') return;
    this.finish();
  }
}

if (!customElements.get('collapsible-description')) {
  customElements.define('collapsible-description', CollapsibleDescription);
}
